import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Input from '@cloudscape-design/components/input';
import FormField from '@cloudscape-design/components/form-field';
import Table from '@cloudscape-design/components/table';
import Link from '@cloudscape-design/components/link';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Alert from '@cloudscape-design/components/alert';
import { useNavigate } from 'react-router-dom';
import { getMyTicketsReport } from '../../api/tickets';
import { MyTicketsReport as MyTicketsData, ReportTicket } from '../../types/ticket';
import { downloadCsv, csvDateStamp } from '../../utils/csvExport';
import { TicketStatusBadge } from '../TicketStatusBadge';
import { SeverityBadge } from '../SeverityBadge';

const DEFAULT_ASSIGNEE = 'marehal';

function TicketLink({ ticketId }: { ticketId: string }) {
  const navigate = useNavigate();
  return (
    <Link onFollow={(e) => { e.preventDefault(); navigate(`/tickets/${ticketId}`); }}>
      {ticketId}
    </Link>
  );
}

const AGING_COLUMNS = [
  { id: 'ticketId', header: 'Ticket', cell: (item: ReportTicket & { daysOpen: number }) => <TicketLink ticketId={item.ticketId} /> },
  { id: 'title', header: 'Title', cell: (item: ReportTicket & { daysOpen: number }) => item.title || '—' },
  { id: 'daysOpen', header: 'Days Open', cell: (item: ReportTicket & { daysOpen: number }) => (
    <StatusIndicator type={item.daysOpen > 14 ? 'error' : item.daysOpen > 7 ? 'warning' : 'success'}>
      {item.daysOpen}d
    </StatusIndicator>
  ) },
  { id: 'status', header: 'Status', cell: (item: ReportTicket & { daysOpen: number }) => <TicketStatusBadge status={item.status} /> },
  { id: 'severity', header: 'Severity', cell: (item: ReportTicket & { daysOpen: number }) => item.originalSeverity ? <SeverityBadge severity={item.originalSeverity} /> : '—' },
];

export function MyTicketsReport() {
  const [assignee, setAssignee] = useState(DEFAULT_ASSIGNEE);
  const [inputValue, setInputValue] = useState(DEFAULT_ASSIGNEE);
  const [report, setReport] = useState<MyTicketsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async (alias: string) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await getMyTicketsReport(alias));
    } catch (e: any) {
      setError(e.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(assignee);
  }, [assignee, fetchReport]);

  const handleDownload = () => {
    if (!report) return;
    const rows = report.aging.map((t) => [
      t.ticketId, t.title, t.status, t.originalSeverity, t.daysOpen, t.createdAt,
    ]);
    downloadCsv(
      `my-tickets-${report.assignee}-${csvDateStamp()}.csv`,
      ['ticketId', 'title', 'status', 'severity', 'daysOpen', 'createdAt'],
      rows,
    );
  };

  const mttrBetterThanTeam =
    report?.myMttr !== null && report?.teamMttr !== null &&
    report !== null && report.myMttr! <= report.teamMttr!;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button iconName="download" onClick={handleDownload} disabled={!report}>
                Download CSV
              </Button>
            }
          >
            My Tickets
          </Header>
        }
      >
        <SpaceBetween size="m">
          <FormField label="Assignee alias">
            <SpaceBetween direction="horizontal" size="xs">
              <Input
                value={inputValue}
                onChange={({ detail }) => setInputValue(detail.value)}
                placeholder="e.g., marehal"
              />
              <Button onClick={() => setAssignee(inputValue.trim())} disabled={!inputValue.trim()}>
                Load
              </Button>
            </SpaceBetween>
          </FormField>

          {loading && <Box textAlign="center" padding="l"><Spinner size="large" /></Box>}
          {error && <Alert type="error">Failed to load report: {error}</Alert>}

          {report && !loading && (
            <KeyValuePairs
              columns={4}
              items={[
                { label: 'Open Tickets', value: report.totalOpen },
                { label: 'Resolved This Week', value: report.resolvedThisWeek },
                {
                  label: 'My MTTR (30d)',
                  value: report.myMttr === null ? '—' : (
                    <StatusIndicator type={mttrBetterThanTeam ? 'success' : 'warning'}>
                      {report.myMttr} days
                    </StatusIndicator>
                  ),
                },
                { label: 'Team MTTR (30d)', value: report.teamMttr === null ? '—' : `${report.teamMttr} days` },
              ]}
            />
          )}
        </SpaceBetween>
      </Container>

      {report && !loading && (
        <>
          <Container header={<Header variant="h3">By Status</Header>}>
            <SpaceBetween size="s">
              {Object.entries(report.byStatus).map(([status, tickets]) => (
                <ExpandableSection
                  key={status}
                  headerText={`${status} (${tickets.length})`}
                >
                  <SpaceBetween size="xs">
                    {tickets.map((t) => (
                      <Box key={t.ticketId}>
                        <TicketLink ticketId={t.ticketId} /> — {t.title}
                      </Box>
                    ))}
                  </SpaceBetween>
                </ExpandableSection>
              ))}
              {Object.keys(report.byStatus).length === 0 && (
                <Box color="text-body-secondary">No open tickets 🎉</Box>
              )}
            </SpaceBetween>
          </Container>

          <Container header={<Header variant="h3" counter={`(${report.aging.length})`}>Aging Tickets (oldest first)</Header>}>
            <Table
              items={report.aging}
              columnDefinitions={AGING_COLUMNS}
              variant="embedded"
              empty={<Box color="text-body-secondary">No aging tickets</Box>}
              trackBy="ticketId"
            />
          </Container>
        </>
      )}
    </SpaceBetween>
  );
}
